import React from "react";
import { Check } from "lucide-react";
import { useQuote } from "../QuoteDialog";

const ROWS = [
  {
    name: '24" Desktop Printer',
    bestFor: "Classrooms, small offices",
    volume: "Low Volume",
    school: "Small Student Population",
    badge: "Entry",
    img: "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/jnvwojdz_PSP%2024D.jpg",
  },
  {
    name: '24" Printer w/ Stand',
    bestFor: "Classrooms, small offices",
    volume: "Low Volume",
    school: "Small Student Population",
    badge: "Entry",
    img: "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/tqvbcegi_PSP%2024.png",
  },
  {
    name: '24" Indoor/Outdoor Printer',
    bestFor: "Posters + light banners",
    volume: "Moderate Volume",
    school: "Medium Student Population",
    badge: "Versatile",
    highlighted: true,
    img: "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/0c6unmsx_PSP%2024IO.jpg",
  },
  {
    name: '36" Indoor/Outdoor Printer',
    bestFor: "Posters + banners + signage",
    volume: "High Volume",
    school: "Large Student Population",
    badge: "Pro",
    img: "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/41pladw1_PSP%2036IO.webp",
  },
  {
    name: '44" Indoor/Outdoor Printer',
    bestFor: "Full-scale banner production",
    volume: "High Volume",
    school: "Large Student Population",
    badge: "Flagship",
    img: "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/r09tv1by_PSP%2044IO.jpg",
  },
];

export default function PrinterTable() {
  const { openQuote } = useQuote();

  return (
    <section
      id="printer-table"
      data-testid="printer-table-section"
      className="bg-white py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
            Print Shop Pro lineup
          </div>
          <h2
            data-testid="printer-table-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
          >
            Choose the Right Printer for Your School
          </h2>
        </div>

        {/* Desktop / tablet table */}
        <div
          data-testid="printer-table-desktop"
          className="hidden md:block overflow-hidden rounded-2xl border border-slate-200 shadow-sm"
        >
          <table className="w-full text-left border-collapse">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-6 py-5 text-xs uppercase tracking-[0.18em] font-bold">
                  Printer Option
                </th>
                <th className="px-6 py-5 text-xs uppercase tracking-[0.18em] font-bold">
                  Best For
                </th>
                <th className="px-6 py-5 text-xs uppercase tracking-[0.18em] font-bold">
                  Printing Volume
                </th>
                <th className="px-6 py-5 text-xs uppercase tracking-[0.18em] font-bold">
                  School Size
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr
                  key={r.name}
                  data-testid={`printer-row-${i}`}
                  className={`border-t border-slate-200 transition-colors ${
                    r.highlighted
                      ? "bg-teal/5 hover:bg-teal/10"
                      : "bg-white hover:bg-[#F4F6F4]"
                  }`}
                >
                  <td className="px-6 py-6">
                    <div className="flex items-center gap-4">
                      <div
                        className={`relative flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border ${
                          r.highlighted ? "border-teal/40" : "border-slate-200"
                        } bg-white flex items-center justify-center p-1.5`}
                      >
                        <img
                          src={r.img}
                          alt={`${r.name} preview`}
                          loading="lazy"
                decoding="async"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex flex-col items-start gap-1.5">
                        <span className="font-serif text-lg font-bold text-navy leading-tight">
                          {r.name}
                        </span>
                        {r.badge && (
                          <span
                            className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 rounded-full ${
                              r.highlighted
                                ? "bg-teal text-white"
                                : "bg-navy/5 text-navy"
                            }`}
                          >
                            {r.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-6 text-slate-700">{r.bestFor}</td>
                  <td className="px-6 py-6 text-slate-700">{r.volume}</td>
                  <td className="px-6 py-6 text-slate-700">{r.school}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div
          data-testid="printer-table-mobile"
          className="md:hidden grid grid-cols-1 gap-4"
        >
          {ROWS.map((r, i) => (
            <div
              key={r.name}
              data-testid={`printer-card-${i}`}
              className={`rounded-2xl border overflow-hidden ${
                r.highlighted
                  ? "border-teal bg-teal/5"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="aspect-[16/10] bg-white overflow-hidden flex items-center justify-center p-4">
                <img
                  src={r.img}
                  alt={`${r.name} preview`}
                  loading="lazy"
                decoding="async"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="font-serif text-xl font-bold text-navy">
                    {r.name}
                  </h3>
                  {r.badge && (
                    <span
                      className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 rounded-full ${
                        r.highlighted ? "bg-teal text-white" : "bg-navy/5 text-navy"
                      }`}
                    >
                      {r.badge}
                    </span>
                  )}
                </div>
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-0.5">
                      Best For
                    </dt>
                    <dd className="text-slate-700">{r.bestFor}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-0.5">
                      Printing Volume
                    </dt>
                    <dd className="text-slate-700">{r.volume}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-0.5">
                      School Size
                    </dt>
                    <dd className="text-slate-700">{r.school}</dd>
                  </div>
                </dl>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8 border-t border-slate-200">
          <p className="flex items-start gap-3 text-slate-700 text-base sm:text-lg max-w-2xl">
            <Check className="w-5 h-5 text-teal mt-1 flex-shrink-0" strokeWidth={3} />
            <span>
              Not sure which one fits your school?{" "}
              <strong className="text-navy">
                We'll recommend the exact setup based on your needs.
              </strong>
            </span>
          </p>
          <button
            data-testid="printer-table-quote-btn"
            onClick={openQuote}
            title="Opens in a new tab"
            className="bg-teal hover:bg-[#0D6BB1] text-white font-bold px-7 py-3.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap"
          >
            Request a Quote
          </button>
        </div>
      </div>
    </section>
  );
}
